import axios from "axios";
import { getAllDataCriteriaApi } from "../lib/api/dataCriteria";
import { CriteriaType, Grade, GradeKoreanName, DataCriteriaResponseType } from "../types/dataCriteria";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";

export interface SensorDataResponse {
  time: string;
  meta: {
    deviceId: string;
    sensorNumber?: string;
  };
  tv: number;
  hv: number;
  ws: number;
  wd: number;
  h2s: number;
  nh3: number;
  ch4: number;
  // 기존 호환성을 위한 필드들
  measuredAt?: string;
  temperature?: number;
  humidity?: number;
  windDirection?: number;
  windSpeed?: number;
  compositeGrade?: string;
}

export class AirQualityService {
  private static instance: AirQualityService;
  private dataCriteriaCache: Map<CriteriaType, DataCriteriaResponseType[]> = new Map();
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30분 캐시 (더 길게)
  private isInitialized: boolean = false;

  public static getInstance(): AirQualityService {
    if (!AirQualityService.instance) {
      AirQualityService.instance = new AirQualityService();
      // 인스턴스 생성 시 즉시 캐시 초기화
      AirQualityService.instance.initializeCache();
    }
    return AirQualityService.instance;
  }

  // 캐시 초기화 (앱 시작 시 한번 실행)
  private async initializeCache(): Promise<void> {
    if (!this.isInitialized) {
      await this.refreshDataCriteriaCache();
      this.isInitialized = true;
      console.log('AirQualityService: Data criteria cache initialized');
    }
  }

  // 문자열 deviceId로 센서 데이터 조회
  async getDeviceSensorDataByString(
    deviceId: string,
    startDate: string,
    endDate: string
  ): Promise<SensorDataResponse[]> {
    try {
      const url = `${API_BASE_URL}/api/sensor-data/devices/${deviceId}/list`;
      const params = { startDate, endDate };
      console.log(`Fetching external API: ${url}`, params);
      
      const response = await axios.get(url, { params });
      console.log(`External API response for device ${deviceId}:`, response.data);
      
      return response.data || [];
    } catch (error) {
      console.error(`Failed to fetch sensor data for device ${deviceId}:`, error);
      return [];
    }
  }

  // 특정 기기의 센서 데이터 조회 (숫자 ID)
  async getDeviceSensorData(
    deviceId: number,
    startDate: string,
    endDate: string
  ): Promise<SensorDataResponse[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/devices/${deviceId}/dev-datas`, {
        params: { startDate, endDate },
      });
      // 새로운 응답 구조에 맞게 변환
      const transformedData = await Promise.all(response.data.data.map(async (item: any) => ({
        ...item,
        measuredAt: item.time,
        temperature: item.tv,
        humidity: item.hv,
        windDirection: item.wd,
        windSpeed: item.ws,
        compositeGrade: await this.calculateCompositeGrade(item.h2s, item.nh3)
      })));
      return transformedData;
    } catch (error) {
      console.error("Failed to fetch sensor data:", error);
      return []; // 실패 시 빈 배열 반환
    }
  }

  // 대시보드용 - 특정 기기의 오늘 센서 데이터 최대 10개만 조회 (빠른 응답)
  async getDeviceTodayData(deviceId: number): Promise<SensorDataResponse[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/devices/${deviceId}/dev-datas?limit=10`);
      // 새로운 응답 구조에 맞게 변환
      const transformedData = await Promise.all(response.data.data.map(async (item: any) => ({
        ...item,
        measuredAt: item.time,
        temperature: item.tv,
        humidity: item.hv,
        compositeGrade: await this.calculateCompositeGrade(item.h2s, item.nh3)
      })));
      return transformedData;
    } catch (error) {
      console.error("Failed to fetch today sensor data:", error);
      return []; // 실패 시 빈 배열 반환
    }
  }

  // 모니터링용 - 특정 기기의 오늘 센서 데이터 전체 조회 (그래프용)
  async getDeviceTodayDataForMonitoring(deviceId: number): Promise<SensorDataResponse[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/devices/${deviceId}/dev-datas`);
      // 새로운 응답 구조에 맞게 변환
      const transformedData = await Promise.all(response.data.data.map(async (item: any) => ({
        ...item,
        measuredAt: item.time,
        temperature: item.tv,
        humidity: item.hv,
        compositeGrade: await this.calculateCompositeGrade(item.h2s, item.nh3)
      })));
      return transformedData;
    } catch (error) {
      console.error("Failed to fetch today sensor data for monitoring:", error);
      return []; // 실패 시 빈 배열 반환
    }
  }

  // 모니터링용 - 특정 기기의 최신 센서 데이터 1개만 조회 (초고속)
  async getDeviceLatestData(deviceId: number): Promise<SensorDataResponse | null> {
    try {
      // 오늘 데이터 중 최신 1개만 가져오기 (limit=1 파라미터 추가 권장)
      const response = await axios.get(`${API_BASE_URL}/api/v1/devices/${deviceId}/dev-datas`, {
        params: { limit: 1 } // 백엔드에서 지원한다면
      });
      
      if (response.data.data && response.data.data.length > 0) {
        const item = response.data.data[0]; // 최신 데이터만
        return {
          ...item,
          measuredAt: item.time,
          temperature: item.tv,
          humidity: item.hv,
          compositeGrade: await this.calculateCompositeGrade(item.h2s, item.nh3)
        };
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch latest sensor data:", error);
      return null;
    }
  }

  // 대시보드용 - deviceId 문자열로 센서 데이터 조회 (V1 API 사용)
  async getDeviceTodayDataByDeviceId(deviceId: string): Promise<SensorDataResponse[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/v1/devices/${deviceId}/dev-datas?limit=10`);
      // 새로운 응답 구조에 맞게 변환
      const transformedData = await Promise.all(response.data.data.map(async (item: any) => ({
        ...item,
        measuredAt: item.time,
        temperature: item.tv,
        humidity: item.hv,
        compositeGrade: await this.calculateCompositeGrade(item.h2s, item.nh3)
      })));
      return transformedData;
    } catch (error) {
      console.error(`Failed to fetch today sensor data for deviceId ${deviceId}:`, error);
      return []; // 실패 시 빈 배열 반환
    }
  }

  // 지역별 평균 계산 (여러 기기 API 호출)
  async getRegionalAverage(
    deviceIds: number[],
    startDate?: string,
    endDate?: string
  ): Promise<{
    avgH2s: number;
    avgNh3: number;
    avgTemperature: number;
    avgHumidity: number;
    compositeGrade: string;
    deviceCount: number;
  }> {
    try {
      // startDate와 endDate가 있으면 기존 방식, 없으면 최신 데이터만 조회 (성능 최적화)
      let latestDataList: SensorDataResponse[] = [];
      
      if (startDate && endDate) {
        // 날짜 범위가 지정된 경우 기존 방식
        const allDataPromises = deviceIds.map((deviceId) =>
          this.getDeviceSensorData(deviceId, startDate, endDate)
        );
        const allResults = await Promise.all(allDataPromises);
        
        allResults.forEach((deviceData) => {
          if (deviceData && deviceData.length > 0) {
            latestDataList.push(deviceData[0]); // 최신 데이터 (가장 첫 번째)
          }
        });
      } else {
        // 날짜 범위가 없으면 최신 데이터만 조회 (빠른 응답)
        const latestDataPromises = deviceIds.map((deviceId) =>
          this.getDeviceLatestData(deviceId)
        );
        const latestResults = await Promise.all(latestDataPromises);
        
        latestResults.forEach((latestData) => {
          if (latestData) {
            latestDataList.push(latestData);
          }
        });
      }

      if (latestDataList.length === 0) {
        return {
          avgH2s: 0,
          avgNh3: 0,
          avgTemperature: 0,
          avgHumidity: 0,
          compositeGrade: "데이터없음",
          deviceCount: 0,
        };
      }

      // 평균 계산
      const avgH2s = this.calculateAverage(
        latestDataList.map((d) => d.h2s).filter((v) => v !== null && v !== undefined)
      );
      const avgNh3 = this.calculateAverage(
        latestDataList.map((d) => d.nh3).filter((v) => v !== null && v !== undefined)
      );
      const avgTemperature = this.calculateAverage(
        latestDataList.map((d) => d.tv).filter((v) => v !== null && v !== undefined)
      );
      const avgHumidity = this.calculateAverage(
        latestDataList.map((d) => d.hv).filter((v) => v !== null && v !== undefined)
      );

      // 복합 등급 계산 (H2S + NH3 기준)
      const compositeGrade = await this.calculateCompositeGrade(avgH2s, avgNh3);

      return {
        avgH2s,
        avgNh3,
        avgTemperature,
        avgHumidity,
        compositeGrade,
        deviceCount: latestDataList.length,
      };
    } catch (error) {
      console.error("Failed to calculate regional average:", error);
      return {
        avgH2s: 0,
        avgNh3: 0,
        avgTemperature: 0,
        avgHumidity: 0,
        compositeGrade: "오류",
        deviceCount: 0,
      };
    }
  }

  // 평균 계산 유틸 함수
  private calculateAverage(values: number[]): number {
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return Math.round((sum / values.length) * 100) / 100; // 소수점 2자리
  }

  // 데이터 기준 캐시 새로고침
  private async refreshDataCriteriaCache(): Promise<void> {
    try {
      const allCriteria = await getAllDataCriteriaApi();
      
      // 타입별로 캐시 저장
      allCriteria.forEach(group => {
        this.dataCriteriaCache.set(group.criteriaType, group.grades);
      });
      
      this.cacheTimestamp = Date.now();
    } catch (error) {
      console.error('Failed to refresh data criteria cache:', error);
    }
  }

  // 데이터 기준 가져오기 (캐시 사용)
  private async getDataCriteria(criteriaType: CriteriaType): Promise<DataCriteriaResponseType[]> {
    // 초기화가 안 된 경우에만 초기화
    if (!this.isInitialized) {
      await this.initializeCache();
    }
    
    const now = Date.now();
    
    // 캐시가 만료된 경우에만 새로고침 (30분 후)
    if ((now - this.cacheTimestamp) > this.CACHE_DURATION) {
      console.log('AirQualityService: Cache expired, refreshing...');
      await this.refreshDataCriteriaCache();
    }
    
    return this.dataCriteriaCache.get(criteriaType) || [];
  }

  // 복합 등급 계산 (동적 기준 사용)
  private async calculateCompositeGrade(h2s: number, nh3: number): Promise<string> {
    try {
      // 각 기기별 종합 측정물질 기준 사용
      const criteria = await this.getDataCriteria(CriteriaType.DEVICE_TOTAL);
      
      if (criteria.length === 0) {
        // 기준이 없으면 기본값 반환
        if (h2s <= 5 && nh3 <= 50) return "좋음";
        if (h2s <= 20 && nh3 <= 200) return "보통";
        if (h2s <= 100 && nh3 <= 1000) return "나쁨";
        return "매우나쁨";
      }

      // 등급을 순서대로 확인 (GOOD -> NORMAL -> BAD -> VERY_BAD)
      const orderedGrades = [Grade.GOOD, Grade.NORMAL, Grade.BAD, Grade.VERY_BAD];
      
      for (const grade of orderedGrades) {
        const criterion = criteria.find(c => c.grade === grade && c.isActive);
        if (criterion) {
          const h2sThreshold = Number(criterion.h2sValue);
          const nh3Threshold = Number(criterion.nh3Value);
          
          if (criterion.comparisonType === '이하') {
            if (h2s <= h2sThreshold && nh3 <= nh3Threshold) {
              return criterion.gradeKoreanName;
            }
          } else if (criterion.comparisonType === '이상') {
            if (h2s >= h2sThreshold && nh3 >= nh3Threshold) {
              return criterion.gradeKoreanName;
            }
          }
        }
      }
      
      // 어떤 기준에도 맞지 않으면 최악 등급 반환
      return GradeKoreanName[Grade.VERY_BAD];
      
    } catch (error) {
      console.error('Failed to calculate composite grade:', error);
      // 에러 시 기본 계산 사용
      if (h2s <= 5 && nh3 <= 50) return "좋음";
      if (h2s <= 20 && nh3 <= 200) return "보통";
      if (h2s <= 100 && nh3 <= 1000) return "나쁨";
      return "매우나쁨";
    }
  }

  // 실시간 장치현황 조회
  async getRealTimeDeviceStatus(): Promise<{
    totalDevices: number;
    onlineDevices: number;
    offlineDevices: number;
    onlineRate: number;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/devices/realtime-status`);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch real-time device status:", error);
      return {
        totalDevices: 0,
        onlineDevices: 0,
        offlineDevices: 0,
        onlineRate: 0,
      };
    }
  }

  // 지역별 종합 센서 데이터 조회 (서버에서 직접 계산된 평균)
  async getRegionSensorSummary(regionCode: string): Promise<{
    avgH2s: number;
    avgNh3: number;
    avgTemperature: number;
    avgHumidity: number;
    compositeGrade: string;
    deviceCount: number;
    regionCode: string;
  }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/sensor-data/regions/${regionCode}/summary`);
      
      // 응답 구조 확인 및 데이터 추출
      console.log("Raw API response:", response.data);
      
      let data;
      if (response.data.data) {
        // 래퍼가 있는 경우
        data = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
      } else {
        // 직접 객체인 경우
        data = response.data;
      }
      
      console.log("Extracted data:", data);
      
      if (!data) {
        return {
          avgH2s: 0,
          avgNh3: 0,
          avgTemperature: 0,
          avgHumidity: 0,
          compositeGrade: "데이터없음",
          deviceCount: 0,
          regionCode,
        };
      }

      const compositeGrade = await this.calculateCompositeGrade(data.h2s || data.avgH2s || 0, data.nh3 || data.avgNh3 || 0);

      return {
        avgH2s: data.avgH2s || data.h2s || 0,
        avgNh3: data.avgNh3 || data.nh3 || 0,
        avgTemperature: data.avgTemperature || data.tv || 0,
        avgHumidity: data.avgHumidity || data.hv || 0,
        compositeGrade,
        deviceCount: data.deviceCount || 0,
        regionCode,
      };
    } catch (error) {
      console.error(`Failed to fetch region sensor summary for ${regionCode}:`, error);
      return {
        avgH2s: 0,
        avgNh3: 0,
        avgTemperature: 0,
        avgHumidity: 0,
        compositeGrade: "오류",
        deviceCount: 0,
        regionCode,
      };
    }
  }
}
